import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import SessionExpiryToast from "./components/SessionExpiryToast";
import { overridePvProductConfig } from "./config/pv-product-config";
import { overrideGeneralProductCatalog } from "./config/general-product-config";
import { loadLiveRules } from "./lib/sizing-profile-shared";
import { API_BASE } from "./lib/api-base";

import LandingPage from "./pages/LandingPage";
import GeneralConfigurator from "./pages/GeneralConfigurator";
import PvConfigurator from "./pages/PvConfigurator";
import LeadForm from "./pages/LeadForm";
import FragHansPage from "./pages/FragHansPage";

import DashboardLogin from "./pages/dashboard/DashboardLogin";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import KnowledgeBase from "./pages/dashboard/KnowledgeBase";
import PlaceholderPage from "./pages/dashboard/PlaceholderPage";

import SettingsPage from "./pages/dashboard/SettingsPage";
import TicketsPage from "./pages/dashboard/TicketsPage";
import TasksPage from "./pages/dashboard/TasksPage";
import ConfiguratorAdminPage from "./pages/dashboard/ConfiguratorAdminPage";
import DocumentsPage from "./pages/dashboard/DocumentsPage";
import InquiriesPage from "./pages/dashboard/InquiriesPage";
import OrdersPage from "./pages/dashboard/OrdersPage";
import PhoneAssistantPage from "./pages/dashboard/PhoneAssistantPage";
import TeamCalendarPage from "./pages/dashboard/TeamCalendarPage";
import SystemDatabasePage from "./pages/dashboard/SystemDatabasePage";
import InventoryPage from "./pages/dashboard/InventoryPage";
import DocumentArchivePage from "./pages/dashboard/DocumentArchivePage";
import ShopPage from "./pages/dashboard/ShopPage";
import MyOrdersPage from "./pages/dashboard/MyOrdersPage";
import BestellungenPage from "./pages/dashboard/BestellungenPage";
import SystemCheckPage from "./pages/dashboard/SystemCheckPage";

function App() {
  // Bootstrap: load live PV system config (MPPT specs) from the backend so
  // admin edits in /dashboard/konfigurator take effect without redeploy.
  useEffect(() => {
    const API = API_BASE;
    fetch(`${API}/api/configurator/pv-systems`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) overridePvProductConfig(d.systems, d.evalOrder); })
      .catch(() => { /* silently fall back to static defaults */ });
    // 04.02.2026: Zentrale System-Registry — jedes im Admin-Konfigurator
    // angelegte System (auch nicht-PV wie HS 18) wird hier geholt und
    // überschreibt die statische `generalProductCatalog`. So sind neue
    // Systeme sofort in Frag HANS + Systemkonfigurator sichtbar.
    fetch(`${API}/api/configurator/general-systems`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) overrideGeneralProductCatalog(d.systems); })
      .catch(() => { /* fallback: statische Liste */ });
    // Lade ggf. Admin-bearbeitete Sizing-Regeln (BASE_FACTORS, UPLIFTS, LIMITS)
    // — wirkt sofort in `recommendFactor()` ohne Redeploy.
    loadLiveRules(API);
  }, []);

  return (
    <TooltipProvider>
      <AuthProvider>
        <SessionExpiryToast />
        <Toaster />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/konfigurator" element={<GeneralConfigurator />} />
            <Route path="/pv-konfigurator" element={<PvConfigurator />} />
            <Route path="/anfrage" element={<LeadForm />} />
            <Route path="/frag-hans" element={<FragHansPage />} />

            {/* Dashboard routes */}
            <Route path="/dashboard/login" element={<DashboardLogin />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardHome />} />
              <Route path="wissensdatenbank" element={<KnowledgeBase />} />
              <Route path="dokumente" element={<DocumentsPage />} />
              <Route path="tickets" element={<TicketsPage />} />
              <Route path="aufgaben" element={<TasksPage />} />
              <Route path="konfigurator" element={<ConfiguratorAdminPage />} />
              <Route path="angebote" element={<InquiriesPage />} />
              <Route path="auftraege" element={<OrdersPage />} />
              <Route path="telefonassistent" element={<PhoneAssistantPage />} />
              <Route path="kalender" element={<TeamCalendarPage />} />
              <Route path="systemdatenbank" element={<SystemDatabasePage />} />
              <Route path="lager" element={<InventoryPage />} />
              <Route path="dokumente-archiv" element={<DocumentArchivePage />} />
              <Route path="shop" element={<ShopPage />} />
              <Route path="systempruefung" element={<SystemCheckPage />} />
              <Route path="meine-bestellungen" element={<MyOrdersPage />} />
              <Route path="bestellungen" element={<BestellungenPage />} />
              <Route path="einstellungen" element={<SettingsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  );
}

export default App;
